import React from 'react';
import {Body, Icon} from '@lightricks/react-design-system';
import type {IconName} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {ROUTES} from '@/config/routesConstants';
import Link from '@/components/link';
import {CampaignListItem} from '@/hooks/queries/use-campaigns-v2-list-query/useCampaignsV2ListQuery';
import styles from './CampaignStatBoxes.module.scss';

interface CampaignStatBoxesProps {
  campaign: CampaignListItem;
}

interface StatActionProps {
  icon: React.ReactNode;
  label: string;
  href: string;
}

// RDS only exposes small (16) / medium (24) / large (32). Figma calls for
// 20px so we render at "medium" and clamp the box + font-size down to 20.
const ICON_SIZE_OVERRIDE = {width: 20, height: 20, fontSize: 20};

function StatAction({icon, label, href}: StatActionProps) {
  return (
    <Link
      to={href}
      className={styles.statAction}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={styles.statContent}>
        {icon}
        <Body size="lg" className={styles.statLabel}>
          {label}
        </Body>
      </div>
    </Link>
  );
}

function RdsIcon({name}: {name: IconName}) {
  return (
    <Icon
      name={name}
      size="medium"
      appearance="neutral"
      style={ICON_SIZE_OVERRIDE}
    />
  );
}

export default function CampaignStatBoxes({campaign}: CampaignStatBoxesProps) {
  const {
    id,
    newMessagesCount: messageCount = 0,
    newApplicationsCount: applicationCount = 0,
    assetsCount: assetCount = 0,
  } = campaign;

  const creatorsUrl = `${ROUTES.CAMPAIGNS_V2}/${id}/creators`;
  const assetsUrl = `${ROUTES.CAMPAIGNS_V2}/${id}/assets`;

  // Uses i18next plural suffixes (`_one` / `_other` defined in en.json) so
  // the count is interpolated and the singular/plural form is picked
  // automatically per the count value.
  const stats = [
    {
      key: 'messages',
      icon: <RdsIcon name="Actions-Comment1" />,
      label: translate('new_messages_count', {count: messageCount}),
      count: messageCount,
      href: creatorsUrl,
    },
    {
      key: 'applications',
      icon: <RdsIcon name="Navigation-Profile" />,
      label: translate('new_applications_count', {count: applicationCount}),
      count: applicationCount,
      href: creatorsUrl,
    },
    {
      key: 'assets',
      icon: <RdsIcon name="Navigation-Photos-Light" />,
      label: translate('new_assets_count', {count: assetCount}),
      count: assetCount,
      href: assetsUrl,
    },
  ].filter((stat) => stat.count > 0);

  if (stats.length === 0) {
    return null;
  }

  return (
    <div className={styles.statsRow} data-testid="campaign-stat-actions">
      {stats.map((stat) => (
        <StatAction
          key={stat.key}
          icon={stat.icon}
          label={stat.label}
          href={stat.href}
        />
      ))}
    </div>
  );
}
